// src/services/templateRedline.js
//
// What changed between an older version of a visit and the visit as it stands
// now — the marked-up "redline" a reviewer reads before deciding whether an
// old version is still worth restoring.
//
// Questions are matched by ID, not by position. A question keeps its id for
// its whole life (see makeQuestion), so moving one down the form is a MOVE and
// not a delete-plus-add — which is the difference between a redline a reviewer
// trusts and one that reports twelve changes because a heading was dragged.
//
// Pure and framework-free, so it can be unit-tested without React or Firebase.

import { buildQuestionDiff } from "./templateAuditService";

/**
 * @typedef {object} RedlineRow
 * @property {"unchanged"|"added"|"removed"|"changed"|"moved"} status
 * @property {object} question  the CURRENT question (or the old one, if removed)
 * @property {object} [before]  the old question, when it still exists
 * @property {Array}  [changes] field-level diff, for "changed"
 */

/**
 * Compare an older snapshot of a visit's questions against the current ones.
 *
 * The rows come back in CURRENT document order, with removed questions slotted
 * in where they used to sit — so the result reads as the form does, with the
 * deletions visible in place rather than collected in a footnote.
 *
 * @param {Array} before  the version's questions
 * @param {Array} after   the visit's questions right now
 * @returns {{rows: RedlineRow[], summary: {added: number, removed: number, changed: number, moved: number}}}
 */
export const buildVisitRedline = (before = [], after = [], labelById) => {
  const beforeList = Array.isArray(before) ? before.filter(Boolean) : [];
  const afterList = Array.isArray(after) ? after.filter(Boolean) : [];

  const beforeById = new Map(beforeList.map((q) => [q.id, q]));
  const afterById = new Map(afterList.map((q) => [q.id, q]));
  const beforeIndex = new Map(beforeList.map((q, i) => [q.id, i]));
  const afterIndex = new Map(afterList.map((q, i) => [q.id, i]));

  const rows = [];
  const summary = { added: 0, removed: 0, changed: 0, moved: 0 };

  // Removed questions are re-inserted at the position they held in the OLD
  // version, so a reviewer sees the gap where the question used to be.
  const removed = beforeList.filter((q) => !afterById.has(q.id));
  const removedByBeforeIndex = new Map(
    removed.map((q) => [beforeIndex.get(q.id), q])
  );

  /** Any removed questions that sat before old-position `upTo`, in order. */
  let emittedRemoved = 0;
  const flushRemovedBefore = (upTo) => {
    [...removedByBeforeIndex.keys()]
      .filter((i) => i < upTo)
      .sort((a, b) => a - b)
      .forEach((i) => {
        const q = removedByBeforeIndex.get(i);
        removedByBeforeIndex.delete(i);
        rows.push({ status: "removed", question: q, before: q });
        summary.removed += 1;
        emittedRemoved += 1;
      });
  };

  afterList.forEach((q) => {
    const prev = beforeById.get(q.id);
    if (prev) flushRemovedBefore(beforeIndex.get(q.id));

    if (!prev) {
      rows.push({ status: "added", question: q });
      summary.added += 1;
      return;
    }
    const changes = buildQuestionDiff(prev, q, labelById);
    if (changes.length) {
      rows.push({ status: "changed", question: q, before: prev, changes });
      summary.changed += 1;
      return;
    }
    // Same content, different place. Compared on position AMONG SURVIVING
    // questions — measuring against raw indexes would report every question
    // after a deletion as moved, which is noise, not a change.
    const survivingBefore = beforeList.filter((x) => afterById.has(x.id));
    const survivingAfter = afterList.filter((x) => beforeById.has(x.id));
    const wasAt = survivingBefore.findIndex((x) => x.id === q.id);
    const nowAt = survivingAfter.findIndex((x) => x.id === q.id);
    if (wasAt !== nowAt) {
      rows.push({ status: "moved", question: q, before: prev });
      summary.moved += 1;
      return;
    }
    rows.push({ status: "unchanged", question: q, before: prev });
  });

  // Anything removed from the tail of the old version.
  flushRemovedBefore(Number.MAX_SAFE_INTEGER);
  void emittedRemoved;
  void afterIndex;

  return { rows, summary };
};

/**
 * Split redline rows into the headings they sit under, so the comparison reads
 * the way the form does. A flat list of forty rows is technically complete and
 * practically unreadable — "which section is this in?" is the first thing
 * anyone asks about a change.
 *
 * A heading is itself a row (it can be added, removed or renamed like anything
 * else), so it appears BOTH as the group's heading and with its own status.
 * Rows before the first heading go into a group with no heading.
 *
 * @param {RedlineRow[]} rows  from buildVisitRedline
 * @returns {Array<{heading: RedlineRow|null, rows: RedlineRow[], changed: number}>}
 */
export const groupRedlineByHeading = (rows = []) => {
  const groups = [];
  let current = { heading: null, rows: [], changed: 0 };
  const push = () => {
    if (current.heading || current.rows.length) groups.push(current);
  };
  rows.forEach((r) => {
    if (r.question?.type === "Header") {
      push();
      current = { heading: r, rows: [], changed: r.status === "unchanged" ? 0 : 1 };
      return;
    }
    current.rows.push(r);
    if (r.status !== "unchanged") current.changed += 1;
  });
  push();
  return groups;
};

/** True when nothing at all differs — lets the UI say so plainly instead of
 * rendering a redline with no red in it. */
export const redlineIsEmpty = (summary) =>
  !summary || (!summary.added && !summary.removed && !summary.changed && !summary.moved);

/** One-line count, e.g. "2 added · 1 removed · 3 changed". */
export const describeRedline = (summary) => {
  if (redlineIsEmpty(summary)) return "No changes";
  return [
    summary.added ? `${summary.added} added` : "",
    summary.removed ? `${summary.removed} removed` : "",
    summary.changed ? `${summary.changed} changed` : "",
    summary.moved ? `${summary.moved} moved` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

// ---------------------------------------------------------------------------
// Which version a version is measured against
//
// A version's "what changed" is read against the PREVIOUS APPROVED version of
// the same visit — Version 2.0 is the work done since Version 1.0. That is the
// question a reviewer holding an approved source document actually has, and it
// matches how the exported header numbers the document (see countVisitApprovals
// in templateVersionService.js, which counts the same `kind: "approved"`
// snapshots these helpers order).
//
// Both are pure list arithmetic over snapshots that are already stored, so
// they work on history recorded long before this existed — nothing new is
// written at approval time.
// ---------------------------------------------------------------------------

/** This visit's approved snapshots, oldest first — the numbered versions. */
const approvedForEvent = (versions, eventId) =>
  (Array.isArray(versions) ? versions : [])
    .filter((v) => v && v.kind === "approved" && String(v.eventId || "") === eventId)
    .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")));

/**
 * The approved version a given snapshot should be compared against: the newest
 * approved snapshot OF THE SAME VISIT taken strictly before it.
 *
 * Scoped by eventId, always. A baseline from another visit would produce a
 * redline where every question is added and every question is removed, and it
 * would quietly undo the per-visit split the rest of this feature is built on.
 *
 * Returns null when there is nothing before it — the first approval of a
 * visit, or a built/backup snapshot taken before any approval. The caller
 * says "initial version" rather than rendering a diff against nothing.
 *
 * @param {Array} versions  every version for the template (any order)
 * @param {object} version  the snapshot being read
 * @returns {object|null}
 */
export const previousApprovedVersion = (versions, version) => {
  const eventId = String(version?.eventId || "");
  if (!eventId || !version) return null;
  const createdAt = String(version.createdAt || "");
  const earlier = approvedForEvent(versions, eventId).filter(
    (v) => v.id !== version.id && String(v.createdAt || "") < createdAt
  );
  return earlier[earlier.length - 1] || null;
};

/**
 * Which numbered version this snapshot is — 1 for a visit's first approval, 2
 * for the next, and so on. 0 for anything that isn't an approval, since built
 * and backup snapshots are not versions of the document anyone signed.
 *
 * @param {Array} versions
 * @param {object} version
 * @returns {number}
 */
export const approvalNumberOf = (versions, version) => {
  if (!version || version.kind !== "approved") return 0;
  const eventId = String(version.eventId || "");
  if (!eventId) return 0;
  const idx = approvedForEvent(versions, eventId).findIndex((v) => v.id === version.id);
  return idx < 0 ? 0 : idx + 1;
};

/** How that number prints — the same "Version N.0" the export header uses. */
export const versionLabel = (n) => (n > 0 ? `Version ${n}.0` : "");

/**
 * This visit's most recent approved version, or null if it has never been
 * approved. Same eventId scoping as previousApprovedVersion — a visit is only
 * ever measured against its own history.
 * @param {Array} versions
 * @param {string} eventId
 * @returns {object|null}
 */
export const latestApprovedVersionFor = (versions, eventId) => {
  const list = approvedForEvent(versions, String(eventId || ""));
  return list[list.length - 1] || null;
};

// ---------------------------------------------------------------------------
// "Is this visit exactly what was approved?"
//
// Answered so an unchanged visit can't be approved again. A re-approval that
// changes nothing still writes a version, bumps the visit to "Version 3.0",
// and puts a fresh sign-off date on a document nobody edited — three claims
// that are not true, in the part of the record a monitor reads first.
//
// Deliberately CONTENT-based rather than a dirty flag: an edit that is undone
// leaves the visit exactly as approved, and the record has to agree with what
// is on screen, not with how many times someone typed.
// ---------------------------------------------------------------------------

/** Keys that always differ between an approved snapshot and the live visit
 * (or say nothing about content), so they are not part of "the same". */
const NON_CONTENT_EVENT_KEYS = new Set([
  "id",
  "questions", // compared through the redline instead
  "status",
  "approvedAt",
  "approvedBy",
  // "Finalized on paper" is a note about where the real copy lives, not a
  // change to the document. Counted as content it would make an approved visit
  // read as edited and demand a re-approval for ticking a checkbox.
  "paperFinal",
  "paperFinalAt",
  "paperFinalBy",
]);

/** JSON with keys sorted at every depth — Firestore hands map keys back
 * alphabetically while the editor builds them in insertion order, so raw
 * JSON.stringify reports untouched values as different. Arrays keep their
 * order: there it is meaning. Mirrors templateAuditService's stableJson. */
const stableJson = (v) => {
  if (v === null || typeof v !== "object") return JSON.stringify(v) ?? "null";
  if (Array.isArray(v)) return `[${v.map(stableJson).join(",")}]`;
  return `{${Object.keys(v)
    .sort()
    .filter((k) => v[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stableJson(v[k])}`)
    .join(",")}}`;
};

/** Everything about a visit EXCEPT its questions: name, orientation, log
 * flag, signature blocks. All of it prints, so all of it is a change. */
const eventMeta = (event) =>
  stableJson(
    Object.fromEntries(
      Object.entries(event || {})
        .filter(([k]) => !NON_CONTENT_EVENT_KEYS.has(k))
        .map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    )
  );

/**
 * Whether two versions of a visit are the same document.
 *
 * Questions are compared through the redline, so this agrees with what the
 * "What changed" view shows: if the button says there is nothing to approve,
 * that view says there is nothing to see. Everything else on the event is
 * compared structurally.
 *
 * @param {object} a  the approved snapshot's event
 * @param {object} b  the visit as it stands
 * @returns {boolean}
 */
export const sameVisitContent = (a, b) => {
  if (!a || !b) return false;
  if (eventMeta(a) !== eventMeta(b)) return false;
  const { summary } = buildVisitRedline(a.questions || [], b.questions || []);
  return redlineIsEmpty(summary);
};
